import React, { useState } from 'react';
import {
  FaCheckDouble,
  GiVendingMachine,
  GrGateway,
  GrServerCluster,
  AiOutlineAudit,
  SiAmazonsimpleemailservice,
} from '@/components/atoms/Icons';
import { ActivityLogEntry } from '@/pages/Alert/alerts.interface';
import {
  ALERT_MESSAGES,
  COMMON_STEPPER_BG_COLORS,
  AlertStepActionStatus,
  parseAlertStepperData,
  EXCLUDED_ALERT_TYPES,
  getLabelOfAlertStep,
  ALERT_STEP_SUCCESS_MESSAGES,
  ALERT_STEP_FAILURE_MESSAGES,
} from '@/constant/alerts/alerts.constant';
import { getDateTime } from '@/utils/dateTime';
import _ from 'lodash';
import { CommonStepper as AlertCommonStepper } from '@/components/elements/Stepper';
import type { StepStatus } from '@/components/elements/Stepper';
import { COMMON_STEPPER_CONTENT } from '@/constant/global/global.constant';
import { Text } from '@/components/atoms/AppText';
import { useTheme } from '@/context/theme.context';
import { APP_HEX, APP_VDI } from '@/constant/theme/colors.constant';
import { ALERT_TEST_IDS as TID } from '@/constant/testIds/alert.testIds';
import { tid } from '@/utils/testId';
import { isValidString } from '@/utils/generic';

interface StepProps {
  iconColor?: string;
  iconElement?: React.ReactNode;
  title: string;
  date: string;
  isActive?: boolean;
  status?: StepStatus;
  tooltipContent?: string;
  detailsContent?: React.ReactNode; // Extra content shown on click
  activityDtm?: string;
  excluded?: boolean; // To exclude step based on provider
  doubleTick?: boolean;
  doubleTickColor?: string;
  testId?: string;
  durationTestId?: string;
  labelTestId?: string;
  dateTestId?: string;
  durationValueTestId?: string;
}


export const AlertsStepperFlow = ({
  rowIndex,
  activityLog,
  providerData,
  triggeredOn,
  sentOn,
  sentClient,
  createdOn,
  alertsConfig,
  excludedAlertType,
  closedOn,
}: {
  rowIndex?: number;
  activityLog: ActivityLogEntry[];
  providerData: { providerId: string; partnerUid: string };
  triggeredOn?: string;
  sentOn?: string;
  sentClient?: string;
  createdOn?: string;
  alertsConfig?: any;
  excludedAlertType?: string;
  closedOn?: string;
}) => {
  const { colors } = useTheme();
  const rowKey = `table-row-${rowIndex ?? 0}`;
  // defensively destructure alertsConfig (it may be undefined or contain empty arrays)
  const {
    alertsConfigFromProvider,
    ENABLED_ALERT_STEPS = [],
    ALERTS_STEPPER_ORDER = [],
    EXCLUDED_ALERT_STEPS = [],
  } = alertsConfig || {};
  const hasProviderSteps =
    Array.isArray(ENABLED_ALERT_STEPS) && ENABLED_ALERT_STEPS.length > 0;

  const excludedAlertTypeUpper =
    typeof excludedAlertType === 'string'
      ? excludedAlertType.toUpperCase()
      : undefined;
  const isExcludedAlertType = excludedAlertTypeUpper
    ? Object.values(EXCLUDED_ALERT_TYPES).some(
      (v: any) => v === excludedAlertTypeUpper,
    )
    : false;

  const stepTimeValueMap: Record<string, string | undefined> = {
    sandstar: triggeredOn,
    vdi: createdOn,
    seed: sentOn,
    sen: sentClient,
  };
  // alertStepperData will give alert config of each step including substeps data (GATEWAY,VMS,CLIENTS,...)
  // ensure we pass a safe array to the generator even if the provider config is missing
  const alertStepperData = parseAlertStepperData(ENABLED_ALERT_STEPS || [], {
    alertLog: activityLog,
  });
  // If provider did not provide any stepper config, show a user-friendly message and avoid rendering the AlertNodeFlow
  if (!hasProviderSteps) {
    return (
      <div className="flex flex-col items-center justify-center py-10 gap-3">
        <div className="w-full max-w-sm px-6 py-5 rounded-2xl border border-dashed border-gray-300 dark:border-gray-700 text-center">
          <Text as="p" size="xl" weight="semibold" color="soft">
            No Alerts Config found for this provider.
          </Text>
          <Text as="p" size="base" color="muted" className="mt-1">
            Alerts configuration is missing or empty — the alert stepper is not
            available.
          </Text>
        </div>
      </div>
    );
  }

  const renderActionStatusList = (actionStatus: any, stepName?: string) => {
    const details = Object.entries(actionStatus).map(([key, value]) => ({
      step: key,
      status: value,
    }));

    return details?.map((detail, index) => {
      // Special handling for GET-SYNC: only show if GET_STATE is enabled in provider config
      if (detail?.step === 'GET-SYNC') {
        const getStateEnabled =
          stepName === 'VMS'
            ? alertsConfigFromProvider?.VMS?.GET_STATE
            : stepName === 'CLIENTS'
              ? alertsConfigFromProvider?.CLIENTS?.GET_STATE
              : false;

        // Hide GET-SYNC if GET_STATE is not enabled
        if (!getStateEnabled) {
          return null;
        }
      }

      const isSuccess = detail?.status === true;
      const isFailed = detail?.status === false;
      const pillCls = isSuccess
        ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-400'
        : isFailed
          ? 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-400'
          : 'bg-indigo-100 text-indigo-700 dark:bg-indigo-900/30 dark:text-indigo-400';
      const statusLabel = isSuccess
        ? 'Success'
        : isFailed
          ? 'Failed'
          : 'Not Started';
      const dot = isSuccess ? '●' : isFailed ? '●' : '○';

      return (
        <div
          className="flex justify-between items-center py-1.5 gap-3 min-w-0"
          key={index}
        >
          <Text
            as="span"
            size="md"
            weight="semibold"
            color="soft"
            className="whitespace-nowrap flex-shrink-0"
          >
            {detail?.step}
          </Text>
          <span
            className={[
              'inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold whitespace-nowrap flex-shrink-0',
              pillCls,
            ].join(' ')}
          >
            <Text as="span" size="2xs" className="leading-none">
              {dot}
            </Text>
            {statusLabel}
          </span>
        </div>
      );
    });
  };

  const getStepStatus = (eachStepSummary: any): StepStatus => {
    const statuses = Object.values(eachStepSummary ?? {});
    if (statuses.length === 0) return 'not-reached';
    if (statuses.some((s) => s === false)) return 'failed';
    if (statuses.every((s) => s === true)) return 'done';
    if (statuses.some((s) => s === true)) return 'active';
    return 'not-reached';
  };

  const getStatusColor = (eachStepSummary: any): string => {
    const statuses = Object.values(eachStepSummary);

    if (statuses.length === 0) {
      return APP_HEX.slate400; // no data → pending/grey
    }

    // 1. FAILURE: any action explicitly failed
    if (statuses.some((s) => s === false)) {
      return COMMON_STEPPER_BG_COLORS.FAILURE; // #E75065
    }

    // 2. COMPLETED: every action succeeded
    if (statuses.every((s) => s === true)) {
      return colors.primary; // done = theme primary
    }

    // 3. IN PROGRESS: at least one action succeeded but not all
    if (statuses.some((s) => s === true)) {
      return colors.primary; // active = theme primary
    }

    // 4. PENDING: all still 'Not Started'
    return APP_HEX.slate400;
  };
  const { GATEWAY, VMS, CLIENTS, eachStepSummary } = alertStepperData;
  const { STEPPER_TITLES, ACTIONS } = COMMON_STEPPER_CONTENT;

  /**
   * Return true when we should show the double-check indicator for a specific step.
   * stepName must be either 'VMS' or 'CLIENTS'.
   * Rules:
   * - For 'CLIENTS': show when CLIENTS GET_STATE is enabled in provider config and CLIENTS.GET_SYNC === true.
   * - For 'VMS': show when VMS GET_STATE is enabled and VMS.GET_SYNC === true, but do NOT show VMS when CLIENTS already satisfied the clients rule
   */
  const getStateDoubleTickIndicator = (
    actionStatus: any,
    alertsConfigFromProvider: any,
    stepName: string,
  ): boolean => {
    const clients = actionStatus?.CLIENTS || {};
    const vms = actionStatus?.VMS || {};
    const isClientStepperEnabled = !!alertsConfigFromProvider?.CLIENTS?.ENABLED;
    const isVmsStepperEnabled = !!alertsConfigFromProvider?.VMS?.ENABLED;
    const VMS_GET_STATE_VALUE_FROM_CONFIG =
      !!alertsConfigFromProvider?.VMS?.GET_STATE;
    const CLIENTS_GET_STATE_VALUE_FROM_CONFIG =
      !!alertsConfigFromProvider?.CLIENTS?.GET_STATE;

    const clientsSatisfied =
      isClientStepperEnabled &&
      CLIENTS_GET_STATE_VALUE_FROM_CONFIG &&
      clients?.[ACTIONS.CLIENT_STEP_ACTIONS.GET_SYNC] === true;
    const vmsSatisfied =
      isVmsStepperEnabled &&
      VMS_GET_STATE_VALUE_FROM_CONFIG &&
      vms?.[ACTIONS.VMS_STEP_ACTIONS.GET_SYNC] === true;
    if (stepName === STEPPER_TITLES.CLIENTS) {
      return !!clientsSatisfied;
    }
    // stepName === VMS
    if (stepName === STEPPER_TITLES.VMS) {
      return !!(vmsSatisfied && !clientsSatisfied);
    }

    return false;
  };
  const getHoverContentForEachStep = (
    eachStepActionStatus: AlertStepActionStatus = {},
    stepName: string,
    serverMessage?: string | null,
    actionMessages: Record<string, string | null> = {},
  ) => {
    const gatewayActions = ACTIONS.GATEWAY_STEP_ACTIONS;
    const vmsActions = ACTIONS.VMS_STEP_ACTIONS;
    const clientActions = ACTIONS.CLIENT_STEP_ACTIONS;
    const successMap = ALERT_STEP_SUCCESS_MESSAGES as unknown as Record<
      string,
      Record<string, string>
    >;
    const failureMap = ALERT_STEP_FAILURE_MESSAGES as unknown as Record<
      string,
      Record<string, string>
    >;
    const actionLabels = successMap[stepName] || {};
    const failureLabels = failureMap[stepName] || {};
    const priorityByStep: Record<string, string[]> = {
      GATEWAY: [gatewayActions.UPDATE, gatewayActions.CREATE],
      VMS: [
        vmsActions.UPDATE_SYNC,
        vmsActions.GET_SYNC,
        vmsActions.CREATE_SYNC,
      ],
      CLIENTS: [
        clientActions.UPDATE_SYNC,
        clientActions.GET_SYNC,
        clientActions.CREATE_SYNC,
      ],
    };
    const priorityList =
      priorityByStep[stepName?.toUpperCase()] || Object.keys(actionLabels);

    for (const actionKey of priorityList) {
      const status = eachStepActionStatus?.[actionKey];
      if (status === true) {
        return actionLabels[actionKey] || 'Step in progress';
      }
      if (status === false) {
        // NEW: Check for the specific action message first before falling back to generic failure message
        const specificMsg = actionMessages?.[actionKey];
        if (specificMsg) return specificMsg;

        return (
          failureLabels[actionKey] ||
          `${actionLabels[actionKey] || 'Action'} - ${ACTIONS.STATUS.FAILED}`
        );
      }
      if (status === 'Not Started') {
        continue;
      }
    }

    if (typeof serverMessage === 'string' && serverMessage.trim() !== '') {
      return serverMessage;
    }

    return 'Step not initiated';
  };
  const alertSteps = [
    {
      stepConfig: ENABLED_ALERT_STEPS[0],
      stepKey: `${ALERTS_STEPPER_ORDER[0] || 'vdi'}-${rowKey}`,
      title: isExcludedAlertType
        ? `VDI`
        : `${ALERTS_STEPPER_ORDER[0]} (${getLabelOfAlertStep(0, ALERTS_STEPPER_ORDER, ENABLED_ALERT_STEPS)})`,
      date: triggeredOn ?? '-',
      status: closedOn ?'done' as StepStatus : 'active' as StepStatus,
      iconColor: colors.primary, // alert creation = always done = theme primary
      iconElement: isExcludedAlertType ? (
        <SiAmazonsimpleemailservice className="w-6 h-6 text-white" />
      ) : (
        <GiVendingMachine className="w-6 h-6 text-white" />
      ),
      tooltipContent: ALERT_MESSAGES.CREATION,
      detailsContent: (
        <div>
          <Text as="p" size="base" weight="semibold" color="soft">
            Alert Created
          </Text>
        </div>
      ),
      testId: tid(TID.STEPPER_STEP(`${ALERTS_STEPPER_ORDER[0]}-${rowKey}`)),
      labelTestId: tid(TID.STEPPER_LABEL(`${ALERTS_STEPPER_ORDER[0]}-${rowKey}`)),
      dateTestId: tid(TID.STEPPER_TIMESTAMP(`${ALERTS_STEPPER_ORDER[0]}-${rowKey}`)),
      excluded: EXCLUDED_ALERT_STEPS?.some(
        (step: { stepTitle: string; label: string }) =>
          step?.stepTitle === ALERTS_STEPPER_ORDER[0],
      ),
    },
    {
      stepConfig: ENABLED_ALERT_STEPS[1],
      stepKey: `${STEPPER_TITLES.GATEWAY}-${rowKey}`,
      title: `${ALERTS_STEPPER_ORDER[1]}`,
      date: createdOn ?? '-',
      isActive: true,
      status: getStepStatus(eachStepSummary?.GATEWAY),
      iconColor: getStatusColor(eachStepSummary?.GATEWAY),
      iconElement: <GrGateway className="w-6 h-6 text-white" />,
      tooltipContent: getHoverContentForEachStep(
        eachStepSummary?.GATEWAY || {},
        STEPPER_TITLES.GATEWAY,
        GATEWAY?.message,
        GATEWAY?.actionMessages,
      ),
      detailsContent: renderActionStatusList(
        eachStepSummary?.GATEWAY,
        'GATEWAY',
      ),
      testId: tid(TID.STEPPER_STEP(`${STEPPER_TITLES.GATEWAY}-${rowKey}`)),
      durationTestId: tid(
        TID.STEPPER_DURATION_TO(`${STEPPER_TITLES.GATEWAY}-${rowKey}`),
      ),
      durationValueTestId: tid(
        TID.STEPPER_DURATION_VALUE(`${STEPPER_TITLES.GATEWAY}-${rowKey}`),
      ),
      labelTestId: tid(TID.STEPPER_LABEL(`${STEPPER_TITLES.GATEWAY}-${rowKey}`)),
      dateTestId: tid(TID.STEPPER_TIMESTAMP(`${STEPPER_TITLES.GATEWAY}-${rowKey}`)),
      excluded: EXCLUDED_ALERT_STEPS?.some(
        (step: { stepTitle: string; label: string }) =>
          step?.stepTitle === ALERTS_STEPPER_ORDER[1],
      ),
    },
    {
      stepConfig: ENABLED_ALERT_STEPS[2],
      stepKey: `${STEPPER_TITLES.VMS}-${rowKey}`,
      title: `${ALERTS_STEPPER_ORDER[2]} (${getLabelOfAlertStep(2, ALERTS_STEPPER_ORDER, ENABLED_ALERT_STEPS)})`,
      date: sentOn ?? '-',
      isActive: false,
      status: getStepStatus(eachStepSummary?.VMS),
      iconColor: getStatusColor(eachStepSummary?.VMS),
      iconElement: <GrServerCluster className="w-6 h-6 text-white" />,
      tooltipContent: getHoverContentForEachStep(
        eachStepSummary?.VMS || {},
        STEPPER_TITLES.VMS,
        VMS?.message,
        VMS?.actionMessages,
      ),
      detailsContent: renderActionStatusList(eachStepSummary?.VMS, 'VMS'),
      doubleTick: getStateDoubleTickIndicator(
        eachStepSummary,
        alertsConfigFromProvider,
        'VMS',
      ),
      testId: tid(TID.STEPPER_STEP(`${STEPPER_TITLES.VMS}-${rowKey}`)),
      durationTestId: tid(
        TID.STEPPER_DURATION_TO(`${STEPPER_TITLES.VMS}-${rowKey}`),
      ),
      durationValueTestId: tid(
        TID.STEPPER_DURATION_VALUE(`${STEPPER_TITLES.VMS}-${rowKey}`),
      ),
      labelTestId: tid(TID.STEPPER_LABEL(`${STEPPER_TITLES.VMS}-${rowKey}`)),
      dateTestId: tid(TID.STEPPER_TIMESTAMP(`${STEPPER_TITLES.VMS}-${rowKey}`)),
      excluded: EXCLUDED_ALERT_STEPS?.some(
        (step: { stepTitle: string; label: string }) =>
          step?.stepTitle === ALERTS_STEPPER_ORDER[2],
      ),
      doubleTickColor: getStatusColor(eachStepSummary?.VMS),
    },
    {
      stepConfig: ENABLED_ALERT_STEPS[3],
      stepKey: `${STEPPER_TITLES.CLIENTS}-${rowKey}`,
      title: `CLIENT (${getLabelOfAlertStep(3, ALERTS_STEPPER_ORDER, ENABLED_ALERT_STEPS)})`,
      date: sentClient ?? '-',
      isActive: false,
      status: getStepStatus(eachStepSummary?.CLIENTS),
      iconColor: getStatusColor(eachStepSummary?.CLIENTS),
      iconElement: <AiOutlineAudit className="w-6 h-6 text-white" />,
      tooltipContent: getHoverContentForEachStep(
        eachStepSummary?.CLIENTS || {},
        STEPPER_TITLES.CLIENTS,
        CLIENTS?.message,
        CLIENTS?.actionMessages,
      ),
      detailsContent: renderActionStatusList(
        eachStepSummary?.CLIENTS,
        'CLIENTS',
      ),
      doubleTick: getStateDoubleTickIndicator(
        eachStepSummary,
        alertsConfigFromProvider,
        'CLIENTS',
      ),
      testId: tid(TID.STEPPER_STEP(`${STEPPER_TITLES.CLIENTS}-${rowKey}`)),
      durationTestId: tid(
        TID.STEPPER_DURATION_TO(`${STEPPER_TITLES.CLIENTS}-${rowKey}`),
      ),
      durationValueTestId: tid(
        TID.STEPPER_DURATION_VALUE(`${STEPPER_TITLES.CLIENTS}-${rowKey}`),
      ),
      labelTestId: tid(TID.STEPPER_LABEL(`${STEPPER_TITLES.CLIENTS}-${rowKey}`)),
      dateTestId: tid(TID.STEPPER_TIMESTAMP(`${STEPPER_TITLES.CLIENTS}-${rowKey}`)),
      excluded: EXCLUDED_ALERT_STEPS?.some(
        (step: { stepTitle: string; label: string }) =>
          step?.stepTitle === ALERTS_STEPPER_ORDER[3],
      ),
      doubleTickColor: getStatusColor(eachStepSummary?.CLIENTS),
    },
  ];

  return (
    <div className="flex flex-col gap-1">
      <AlertCommonStepper
        steps={alertSteps}
        showActiveAnimation={false}
        showActiveClockIcon={false}
        legendItems={[
          { label: 'Open', color: APP_HEX.warning },
          { label: 'Closed', color: colors.primary },
          { label: 'Pending', color: APP_HEX.slate400 },
          { label: 'Failed', color: APP_VDI.failed },
        ]}
      />
    </div>
  );
};
